import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { find, partition, reject } from "lodash/fp";

import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import type {
  PatchSchedulingLinkBody,
  SchedulingLink,
} from "@app/common/types";
import Config from "@app/config";
import { replaceWhere } from "@app/utils/arrays";

const useSchedulingLinks = ({ enabled }: { enabled: boolean }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const schedulingLinksQueryKey = ["schedulingLinks", user?.id];

  const listSchedulingLinksQuery = useQuery({
    queryKey: schedulingLinksQueryKey,
    queryFn: apiClient.schedulingLinks.listSchedulingLinks,
    refetchOnWindowFocus: false,
    enabled: Boolean(user?.id) && enabled,
  });

  const createLinkMutation = useMutation({
    mutationFn: apiClient.schedulingLinks.createSchedulingLink,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: schedulingLinksQueryKey }),
  });

  const updateLinkMutation = useMutation({
    mutationFn: apiClient.schedulingLinks.patchSchedulingLink,
    onMutate: async ({ id, ...updatedAttributes }: PatchSchedulingLinkBody) => {
      await queryClient.cancelQueries({ queryKey: schedulingLinksQueryKey });

      const queryData: SchedulingLink[] =
        queryClient.getQueryData(schedulingLinksQueryKey) ?? [];

      const originalLink = find({ id }, queryData);
      if (originalLink) {
        const updatedLink = { ...originalLink, ...updatedAttributes };
        queryClient.setQueryData(
          schedulingLinksQueryKey,
          replaceWhere(queryData, updatedLink, (link) => link.id === id)
        );
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: schedulingLinksQueryKey }),
  });

  const deleteLinkMutation = useMutation({
    mutationFn: apiClient.schedulingLinks.deleteSchedulingLink,
    onMutate: async (id: SchedulingLink["id"]) => {
      await queryClient.cancelQueries({ queryKey: schedulingLinksQueryKey });

      const queryData: SchedulingLink[] =
        queryClient.getQueryData(schedulingLinksQueryKey) ?? [];
      const updatedData = reject({ id }, queryData);
      queryClient.setQueryData(schedulingLinksQueryKey, updatedData);
    },
    onError: () =>
      queryClient.invalidateQueries({ queryKey: schedulingLinksQueryKey }),
  });

  const [defaultLinks, customLinks] = partition(
    (l) => l.kind === "default",
    listSchedulingLinksQuery.data ?? []
  );

  return {
    canCreateCustomLink: customLinks.length < Config.rssLinks.maxCustom,
    isLoading: listSchedulingLinksQuery.isInitialLoading,
    defaultLinks,
    customLinks,

    createLink: createLinkMutation,
    deleteLink: deleteLinkMutation,
    updateLink: updateLinkMutation,
  };
};

export default useSchedulingLinks;
